import { PurchaseDetailModel } from 'api-hooks/order/model';
import { DocumentTypeEnum } from 'api-hooks/order/travel-document';
import { RefundModel } from 'api-hooks/refund/refund';
import { capitalize } from 'common/utils/string';
import HOCInput from 'components/common/hoc-input';
import { Input } from 'components/elements';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import AgentSelectInput from 'legacy-modules/master/agent/components/select-input';
import RefundOrderSelectInput from 'legacy-modules/refund/components/refund-order-select-input';
import { guestNameTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import {
  HalfContainer,
  SectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';

import PurchaseRefundField from './purchase-refund-field';
import {
  ActivityPurchaseAddOnsType,
  ActivityPurchaseGuestDetailType,
  ActivityPurchaseType,
  CruisePurchaseAddOnsType,
  CruisePurchaseDetailsType,
  CruisePurchaseGuestType,
  CruisePurchaseType,
  DocumentPurchaseAddOnsType,
  DocumentPurchaseGuestType,
  DocumentPurchaseType,
  FlightPurchaseAddOnsType,
  FlightPurchaseGuestType,
  FlightPurchaseType,
  HotelPurchaseAddOnsType,
  HotelPurchaseGuestType,
  HotelPurchaseHotelType,
  HotelPurchaseType,
} from '../form-type';

interface Props {
  refund?: RefundModel;
}

export default function InformationAgentField(props: Props) {
  const { refund } = props;
  const { t } = useTranslation();
  const { setValue, clearErrors, getValues, resetField } = useFormContext();
  const { editable } = useFormState();

  const onChangeRefundOrder = React.useCallback(
    (params: PurchaseDetailModel) => {
      const flightPurchases: FlightPurchaseType[] = (
        params.flightPurchases || []
      ).map((fp) => {
        const flightPurchaseGuests: FlightPurchaseGuestType[] =
          fp.flightPurchaseGuests.map((fpg) => {
            const releasePurchaseItem = refund?.releasePurchase
              ? refund.releasePurchase.releasePurchaseItems.find(
                  (curr) => curr.refundable.id === fpg.id,
                )
              : undefined;
            const guest = fpg.guest;
            return {
              airlineName: fpg.airline.name,
              date: fpg.date,
              checkInStatus: fpg.checkInStatus,
              detail: fpg.detail,
              guestId: guest.id,
              guestName: guestNameTransformer(guest),
              id: fpg.id,
              preferences: [],
              price: fpg.price,
              route: fpg.route,
              ticket: fpg.ticket,
              time: fpg.time,
              isSelected: !!releasePurchaseItem,
              preferenceId: fpg?.preference?.id || '',
              preferenceName: fpg?.preference?.name || '',
              refundPrice: releasePurchaseItem?.refundPrice ?? 0,
            };
          });

        const flightPurchaseAddOns: FlightPurchaseAddOnsType[] =
          fp.flightPurchaseAddOns.map((fpao) => {
            const releasePurchaseAddOn = refund?.releasePurchase
              ? refund.releasePurchase.releasePurchaseAddOns.find(
                  (curr) => curr.addonable.id === fpao.id,
                )
              : undefined;
            return {
              guestName: guestNameTransformer(fpao.guest),
              id: fpao.id,
              price: fpao.price,
              route: fpao.route,
              description: fpao.description,
              isSelected: !!releasePurchaseAddOn,
              refundPrice: releasePurchaseAddOn?.refundPrice ?? 0,
            };
          });

        return {
          id: fp.id,
          bookingCode: fp.bookingCode,
          isSelected: false,
          flightPurchaseAddOns,
          flightPurchaseGuests,
        };
      });
      setValue('flightPurchases', flightPurchases);

      const hotelPurchases: HotelPurchaseType[] = (
        params.hotelPurchases || []
      ).map((hp) => {
        const hotelPurchaseAddOns: HotelPurchaseAddOnsType[] =
          hp.hotelPurchaseAddOns.map((hpao) => {
            const releasePurchaseAddOn = refund?.releasePurchase
              ? refund.releasePurchase.releasePurchaseAddOns.find(
                  (curr) => curr.addonable.id === hpao.id,
                )
              : undefined;
            return {
              description: hpao.description,
              guestId: hpao.guest.id,
              guestName: guestNameTransformer(hpao.guest),
              id: hpao.id,
              price: hpao.price,
              isSelected: !!releasePurchaseAddOn,
              priceUnit: hpao.priceUnit || 0,
              unit: hpao.unit || 0,
              refundPrice: releasePurchaseAddOn?.refundPrice ?? 0,
            };
          });

        const hotelPurchaseGuests: HotelPurchaseGuestType[] =
          hp.hotelPurchaseGuests.map((hpg) => ({
            guestId: hpg.guest.id,
            guestName: guestNameTransformer(hpg.guest),
            id: hpg.id,
            isLead: hpg.isLead,
            preferences: [],
            guestPreferences: (hpg.guest.preferenceStats || []).map((ggps) => ({
              id: ggps.id,
              label: `${ggps.name} ${ggps.count > 1 ? `(X${ggps.count})` : ''}`,
            })),
          }));

        const hotelPurchaseHotels: HotelPurchaseHotelType[] =
          hp.hotelPurchaseHotels.map((hsh) => {
            const releasePurchaseItem = refund?.releasePurchase
              ? refund.releasePurchase.releasePurchaseItems.find(
                  (curr) => curr.refundable.id === hsh.id,
                )
              : undefined;
            return {
              checkInStatus: hsh.checkInStatus,
              checkIn: hsh.checkIn,
              checkOut: hsh.checkOut,
              detail: hsh.detail,
              pax: hsh.pax || 0,
              hotelRoomId: hsh.hotelRoom.id,
              id: hsh.id,
              night: hsh.night,
              price: hsh.price,
              room: hsh.room,
              refundPrice: releasePurchaseItem?.refundPrice ?? 0,
              isSelected: !!releasePurchaseItem,
            };
          });

        const isSelectedItems =
          hotelPurchaseHotels.some((hsh) => !!hsh.isSelected) ||
          hotelPurchaseAddOns.some((hpao) => !!hpao.isSelected);

        return {
          isSelected: !!isSelectedItems,
          id: hp.id,
          hotelConfirmation: hp.hotelConfirmation,
          hotelId: hp.hotel.id,
          isCityTax: hp.isCityTax,
          // remark: hp.remark,
          bookingCode: hp.bookingCode,
          cityTax: hp.cityTax,
          hotelPurchaseGuests,
          hotelPurchaseAddOns,
          hotelPurchaseHotels,
        };
      });

      setValue('hotelPurchases', hotelPurchases);

      const cruisePurchases: CruisePurchaseType[] = (
        params.cruisePurchases || []
      ).map((cp) => {
        //guest
        const cruisePurchaseGuests: CruisePurchaseGuestType[] =
          cp.cruisePurchaseGuests.map((cpg) => {
            return {
              guestId: cpg.guest.id,
              id: cpg.id,
              preferences: [],
              guestName: guestNameTransformer(cpg.guest),
            };
          });
        // details
        const cruisePurchaseDetails: CruisePurchaseDetailsType[] =
          cp.cruisePurchaseDetails.map((cpd) => {
            const releasePurchaseItem = refund?.releasePurchase
              ? refund.releasePurchase.releasePurchaseItems.find(
                  (curr) => curr.refundable.id === cpd.id,
                )
              : undefined;
            const guest = cp.cruisePurchaseGuests.find(
              (cpg) => cpg.id === cpd.cruisePurchaseGuestId,
            )?.guest;
            return {
              basicFare: cpd.basicFare,
              cruiseRoomId: cpd.cruiseRoom.id,
              cruiseRoomNumber: cpd.cruiseRoomNumber,
              deck: cpd.deck,
              description: cpd.description,
              guestId: guest?.id || '',
              id: cpd.id,
              isSelected: !!releasePurchaseItem,
              portCharge: cpd.portCharge,
              preferences: [],
              tax: cpd.tax,
              tipping: cpd.tipping,
              guestName: cp.isGroupedPrice
                ? cp.cruisePurchaseGuests
                    .map((csg) => guestNameTransformer(csg.guest))
                    .join(', ')
                : guestNameTransformer(guest!),
              refundPrice: releasePurchaseItem?.refundPrice ?? 0,
            };
          });
        //addons
        const cruisePurchaseAddOns: CruisePurchaseAddOnsType[] =
          cp.cruisePurchaseAddOns.map((cpao) => {
            const releasePurchaseAddOn = refund?.releasePurchase
              ? refund.releasePurchase.releasePurchaseAddOns.find(
                  (curr) => curr.addonable.id === cpao.id,
                )
              : undefined;
            return {
              description: cpao.description,
              guestId: cpao.guest.id,
              guestName: guestNameTransformer(cpao.guest),
              isSelected: !!releasePurchaseAddOn,
              price: cpao.price,
              id: cpao.id,
              refundPrice: releasePurchaseAddOn?.refundPrice ?? 0,
            };
          });

        const informationCruisePurchase = {
          bookingCode: cp.bookingCode,
          cruiseId: cp.cruise.id,
          cruiseShipId: cp.cruiseShip?.id,
          endedAt: cp.endedAt,
          startedAt: cp.startedAt,
          id: cp.id,
          isSelected: !!refund,
          isGroupedPrice: cp.isGroupedPrice,
          route: cp.route,
          cruiseName: cp.cruise.name,
          cruiseShipName: cp.cruiseShip?.name,
          cruiseRooms: cp.cruise.cruiseRooms.map((room) => ({
            id: room.id,
            label: `${room.type} (${room.size})`,
          })),
        };
        return {
          ...informationCruisePurchase,
          cruisePurchaseAddOns,
          cruisePurchaseGuests,
          cruisePurchaseDetails,
        };
      });

      setValue('cruisePurchases', cruisePurchases);

      const activityPurchases: ActivityPurchaseType[] = (
        params.activityPurchases || []
      ).map((ap) => {
        //guest
        const activityPurchaseGuests: ActivityPurchaseGuestDetailType[] =
          ap.activityPurchaseGuests
            .map((apg) => {
              return apg.activityPurchaseGuestDetails.map((apgd) => {
                const releasePurchaseItem = refund?.releasePurchase
                  ? refund.releasePurchase.releasePurchaseItems.find(
                      (curr) => curr.refundable.id === apgd.id,
                    )
                  : undefined;
                return {
                  guestId: apg.guest.id,
                  guestName: guestNameTransformer(apg.guest),
                  activityId: apgd.activity.id,
                  activityName: apgd.activity.name,
                  date: apgd.date,
                  description: apgd.description,
                  id: apgd.id,
                  isSelected: !!releasePurchaseItem,
                  price: apgd.totalPrice,
                  type: capitalize(apgd.activity.type),
                  refundPrice: releasePurchaseItem?.refundPrice ?? 0,
                };
              });
            })
            .flat();

        //addons
        const activityPurchaseAddOns: ActivityPurchaseAddOnsType[] =
          ap.activityPurchaseAddOns.map((apao) => {
            const releasePurchaseAddOn = refund?.releasePurchase
              ? refund.releasePurchase.releasePurchaseAddOns.find(
                  (curr) => curr.addonable.id === apao.id,
                )
              : undefined;
            return {
              description: apao.description,
              guestId: apao.guest.id,
              guestName: guestNameTransformer(apao.guest),
              id: apao.id,
              isSelected: !!releasePurchaseAddOn,
              price: apao.price,
              refundPrice: releasePurchaseAddOn?.refundPrice ?? 0,
            };
          });

        return {
          id: ap.id,
          isGroupedPrice: ap.isGroupedPrice,
          activityPurchaseAddOns,
          activityPurchaseGuests,
        };
      });

      setValue('activityPurchases', activityPurchases);

      const documentPurchases: DocumentPurchaseType[] = (
        params.documentPurchases || []
      ).map((dp) => {
        const documentPurchaseGuests: DocumentPurchaseGuestType[] = (() => {
          switch (dp.documentType) {
            case DocumentTypeEnum.visa:
              return dp.documentPurchaseVisaGuests
                .map((dpvg) =>
                  dpvg.documentPurchaseVisaGuestDetails.map((detail) => {
                    const releasePurchaseItem = refund?.releasePurchase
                      ? refund.releasePurchase.releasePurchaseItems.find(
                          (curr) => curr.refundable.id === detail.id,
                        )
                      : undefined;
                    return {
                      id: detail.id,
                      guestId: dpvg.guest.id,
                      guestName: guestNameTransformer(dpvg.guest),
                      description: detail.description,
                      price: detail.price,
                      biometric: detail.biometric,
                      appointmentAt: detail.appointmentAt,
                      preferenceName: detail.visaDocument?.name || '', // TODO: adjust
                      refundPrice: releasePurchaseItem?.refundPrice ?? 0,
                      isSelected: !!releasePurchaseItem,
                    };
                  }),
                )
                .flat();
            case DocumentTypeEnum.insurance:
              return dp.documentPurchaseInsuranceGuests
                .map((dpig) =>
                  dpig.details.map((detail) => {
                    const releasePurchaseItem = refund?.releasePurchase
                      ? refund.releasePurchase.releasePurchaseItems.find(
                          (curr) => curr.refundable.id === detail.id,
                        )
                      : undefined;
                    return {
                      id: detail.id,
                      guestId: dpig.guest.id,
                      guestName: guestNameTransformer(dpig.guest),
                      type: detail.type,
                      startAt: detail.startedAt,
                      endAt: detail.endedAt,
                      days: detail.days,
                      company: detail.company,
                      coverArea: detail.coverArea,
                      description: detail.description,
                      price: detail.price,
                      refundPrice: releasePurchaseItem?.refundPrice ?? 0,
                      isSelected: !!releasePurchaseItem,
                    };
                  }),
                )
                .flat();
            case DocumentTypeEnum.passport:
              return dp.documentPurchasePassportGuests.map((dppg) => {
                const releasePurchaseItem = refund?.releasePurchase
                  ? refund.releasePurchase.releasePurchaseItems.find(
                      (curr) => curr.refundable.id === dppg.details.id,
                    )
                  : undefined;

                return {
                  id: dppg.details.id,
                  guestId: dppg.guest.id,
                  guestName: guestNameTransformer(dppg.guest),
                  price: dppg.details.price,
                  type: dppg.details.type,
                  description: dppg.details.description,
                  refundPrice: releasePurchaseItem?.refundPrice ?? 0,
                  isSelected: !!releasePurchaseItem,
                };
              });
            case DocumentTypeEnum.apec_card:
              return dp.documentPurchaseApecCardGuests.map((dppg) => {
                const releasePurchaseItem = refund?.releasePurchase
                  ? refund.releasePurchase.releasePurchaseItems.find(
                      (curr) => curr.refundable.id === dppg.details.id,
                    )
                  : undefined;

                return {
                  id: dppg.details.id,
                  guestId: dppg.guest.id,
                  guestName: guestNameTransformer(dppg.guest),
                  price: dppg.details.price,
                  type: dppg.details.type,
                  description: dppg.details.description,
                  refundPrice: releasePurchaseItem?.refundPrice ?? 0,
                  isSelected: !!releasePurchaseItem,
                };
              });
            default:
              return [];
          }
        })();

        const documentPurchaseAddOns: DocumentPurchaseAddOnsType[] =
          dp.documentPurchaseAddOns.map((dpao) => {
            const releasePurchaseAddOn = refund?.releasePurchase
              ? refund.releasePurchase.releasePurchaseAddOns.find(
                  (curr) => curr.addonable.id === dpao.id,
                )
              : undefined;
            return {
              description: dpao.description,
              guestId: dpao.guest.id,
              guestName: guestNameTransformer(dpao.guest),
              id: dpao.id,
              price: dpao.price,
              isSelected: !!releasePurchaseAddOn,
              refundPrice: releasePurchaseAddOn?.refundPrice ?? 0,
            };
          });

        return {
          documentType: dp.documentType,
          id: dp.id,
          documentPurchaseAddOns,
          documentPurchaseGuests,
        };
      });

      setValue('documentPurchases', documentPurchases);

      if (editable) {
        const _releasePurchase = getValues('releasePurchase');
        const releasePurchase = {
          ..._releasePurchase,
          id: params.id,
          refId: params.refId,
          transactionAt: new Date(params.transactionAt),
          total: params.total,
          paymentChannel: params.paymentChannel?.name || '',
          agent: params.agent.name,
        };
        setValue('releasePurchase', {
          ...releasePurchase,
          previousPurchase: releasePurchase,
        });

        setValue('transactionOrderType', params.transactionType);
        setValue('releasePurchase.agentId', params.agent.id, {
          shouldValidate: true,
          shouldTouch: true,
        });
      }
    },
    [editable, getValues, refund, setValue],
  );

  const orderNumber = getValues('releasePurchase.id');
  React.useEffect(() => {
    if (!orderNumber || orderNumber === '') {
      resetField('releasePurchase');
      resetField('flightPurchases');
      resetField('hotelPurchases');
      resetField('activityPurchases');
      resetField('cruisePurchases');
      resetField('documentPurchases');
    }
  }, [orderNumber, resetField]);

  return (
    <>
      {!refund && (
        <SectionContainer>
          <Text variant="HeadingSmall">{t('refund:agent_refund')}</Text>
          <Input
            type="check-box"
            name="isHideAgent"
            label={t('refund:no_refund')}
            onAfterChange={(val) => {
              setValue('isHideAgent', val);
              setValue('releasePurchase', null);
              clearErrors();
            }}
          />
        </SectionContainer>
      )}
      <HOCInput keys={['isHideAgent']}>
        {({ isHideAgent }) => {
          return (
            <>
              {!isHideAgent && (
                <>
                  <SeparatorVertical $margin={36} />
                  <SectionContainer>
                    <HalfContainer>
                      <HOCInput keys={['releaseSale.id']}>
                        {(values) => {
                          const id = values['releaseSale.id'];
                          return (
                            <RefundOrderSelectInput
                              name="releasePurchase.id"
                              id={id}
                              onAfterDetailChange={onChangeRefundOrder}
                              required
                              disabled={!!props.refund}
                            />
                          );
                        }}
                      </HOCInput>
                    </HalfContainer>
                    <HalfContainer>
                      <PurchaseRefundField />
                    </HalfContainer>
                  </SectionContainer>
                  <SeparatorVertical $margin={16} />
                  <SectionContainer>
                    <HalfContainer>
                      <AgentSelectInput
                        name="releasePurchase.agentId"
                        required
                      />
                    </HalfContainer>
                  </SectionContainer>
                </>
              )}
            </>
          );
        }}
      </HOCInput>
    </>
  );
}
